import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import { qk } from '@/api/keys';
import type { OrderStop } from '@/api/types';
import { useNavigate, useParams, useRouterState } from '@tanstack/react-router';
import { useFieldArray, useForm, type SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react';
import { EU_MEMBER_STATES, orderInputSchema, type OrderInput } from '@kmdx/contracts';
import { t, countryName, STOP_KIND_BG } from '@/i18n';
import { useCreateOrder, useOrder, useUpdateOrder } from '@/api/hooks/orders';
import { usePartners } from '@/api/hooks/catalog';
import type { Place } from '@/api/types';
import { Button } from '@/components/ui/Button';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { Checkbox, DatePicker, Field, Input, Select, Textarea } from '@/components/ui/Field';
import { LoadingBlock } from '@/components/ui/State';
import { PlaceAutocomplete } from '@/components/orders/PlaceAutocomplete';
import { toast } from '@/stores/toast';

const COUNTRY_OPTIONS = EU_MEMBER_STATES.map((code) => ({
  value: code,
  label: `${code} — ${countryName(code)}`,
}));

function emptyStop(kind: 'pickup' | 'delivery'): OrderInput['stops'][number] {
  return {
    kind,
    name: '',
    address: '',
    city: '',
    country: kind === 'pickup' ? 'BG' : 'DE',
    isHardDeadline: false,
  };
}

/**
 * Празно числово поле е „няма", не NaN. С `valueAsNumber` празното поле
 * ставаше NaN, схемата го отхвърляше на скрито място и формата просто не
 * се изпращаше — без грешка пред очите.
 */
const optionalNumber = (v: unknown): number | undefined => {
  if (v === '' || v === null || v === undefined) return undefined;
  const n = Number(String(v).replace(',', '.'));
  return Number.isFinite(n) ? n : undefined;
};

const DEFAULTS: OrderInput = {
  partnerId: '',
  agreedPriceMinor: 0,
  ccy: 'EUR',
  goodsDescription: '',
  totalWeightKg: 1,
  isAdr: false,
  requiresTailLift: false,
  stops: [emptyStop('pickup'), emptyStop('delivery')],
};

/**
 * Създаване и редакция на заявка. Схемата е тази от `@kmdx/contracts` — същата,
 * с която сървърът валидира, така че съобщенията на български са едни и същи
 * на двете места.
 */
export function OrderFormPage() {
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const isEdit = pathname.endsWith('/edit');
  const params = useParams({ strict: false }) as { orderId?: string };
  const orderId = params.orderId;

  const existing = useOrder(isEdit ? orderId : undefined);
  const partners = usePartners();
  const create = useCreateOrder();
  const update = useUpdateOrder(orderId ?? '');
  const queryClient = useQueryClient();

  const form = useForm<OrderInput>({
    resolver: zodResolver(orderInputSchema),
    defaultValues: DEFAULTS,
    mode: 'onBlur',
  });

  const { fields, append, remove, move } = useFieldArray({ control: form.control, name: 'stops' });

  useEffect(() => {
    const order = existing.data;
    if (!isEdit || !order) return;
    form.reset({
      partnerId: order.partnerId,
      customerRef: order.customerRef ?? undefined,
      agreedPriceMinor: order.agreedPriceMinor,
      ccy: order.ccy,
      goodsDescription: order.goodsDescription ?? '',
      totalWeightKg: order.totalWeightKg ?? 1,
      volumeM3: order.volumeM3 ?? undefined,
      packagesCount: order.packagesCount ?? undefined,
      declaredValueMinor: order.declaredValueMinor ?? undefined,
      isAdr: order.isAdr,
      adrPoints: order.adrPoints ?? undefined,
      requiresTailLift: order.requiresTailLift,
      notes: order.notes ?? undefined,
      stops: order.stops.map((stop) => ({
        kind: stop.kind,
        placeId: stop.placeId ?? undefined,
        name: stop.name,
        address: stop.address,
        city: stop.city,
        postcode: stop.postcode ?? undefined,
        country: stop.country,
        lat: stop.lat ?? (stop.latE7 != null ? stop.latE7 / 1e7 : undefined),
        lon: stop.lon ?? (stop.lonE7 != null ? stop.lonE7 / 1e7 : undefined),
        contactName: stop.contactName ?? undefined,
        contactPhone: stop.contactPhone ?? undefined,
        windowFrom: stop.windowFrom ?? undefined,
        windowTo: stop.windowTo ?? undefined,
        isHardDeadline: stop.isHardDeadline,
        reference: stop.reference ?? undefined,
        instructions: stop.instructions ?? undefined,
      })),
    });
  }, [existing.data, isEdit, form]);

  const onSubmit: SubmitHandler<OrderInput> = (values) => {
    if (isEdit && orderId) {
      const { stops, partnerId: _partner, quoteId: _quote, ...fields } = values;
      update.mutate(fields, {
        onSuccess: async (order) => {
          /**
           * Адресите се пазят с отделни заявки — сървърът не приема спирки в
           * общата редакция и досега промените по тях тихо изчезваха.
           */
          try {
            await syncStops(order.id, existing.data?.stops ?? [], stops);
          } catch (error) {
            toast.error('Адресите не се записаха', error instanceof Error ? error.message : undefined);
            return;
          }
          void queryClient.invalidateQueries({ queryKey: qk.orders.all });
          toast.ok(t.orders.updatedOk, order.number);
          void navigate({ to: '/orders/$orderId', params: { orderId: order.id } });
        },
      });
      return;
    }
    create.mutate(values, {
      onSuccess: (order) => {
        toast.ok(t.orders.createdOk, order.number);
        void navigate({ to: '/orders/$orderId', params: { orderId: order.id } });
      },
    });
  };

  const applyPlace = (index: number, place: Place) => {
    form.setValue(`stops.${index}.placeId`, place.id);
    form.setValue(`stops.${index}.name`, place.name);
    form.setValue(`stops.${index}.address`, place.address);
    form.setValue(`stops.${index}.city`, place.city);
    form.setValue(`stops.${index}.country`, place.country);
    if (place.postcode) form.setValue(`stops.${index}.postcode`, place.postcode);
    /** API-то връща градуси (`lat`/`lon`); старите записи — `latE7`. Никога NaN. */
    const lat = place.lat ?? (place.latE7 != null ? place.latE7 / 1e7 : null);
    const lon = place.lon ?? (place.lonE7 != null ? place.lonE7 / 1e7 : null);
    if (lat !== null && Number.isFinite(lat)) form.setValue(`stops.${index}.lat`, lat);
    if (lon !== null && Number.isFinite(lon)) form.setValue(`stops.${index}.lon`, lon);
    if (place.contactName) form.setValue(`stops.${index}.contactName`, place.contactName);
    if (place.contactPhone) form.setValue(`stops.${index}.contactPhone`, place.contactPhone);
  };

  if (isEdit && existing.isLoading) return <LoadingBlock />;

  const errors = form.formState.errors;

  return (
    <form
      className="flex min-h-full flex-col gap-3 p-3 sm:gap-4 sm:p-4"
      onSubmit={form.handleSubmit(onSubmit)}
    >
      <div className="flex items-center gap-3">
        <h1 className="font-display text-lg font-bold tracking-tight">
          {isEdit ? t.orders.editOrder : t.orders.newOrder}
        </h1>
        <div className="ml-auto flex gap-2">
          <Button type="button" variant="ghost" onClick={() => void navigate({ to: '/orders' })}>
            {t.common.cancel}
          </Button>
          <Button type="submit" variant="primary" loading={create.isPending || update.isPending}>
            {t.common.save}
          </Button>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader title={t.orders.stops} subtitle={t.places.savedFirst} />
          <CardBody className="space-y-4">
            {fields.map((field, index) => (
              <div key={field.id} className="rounded-lg border border-line p-3">
                <div className="mb-2 flex items-center gap-2">
                  <Select
                    className="w-40"
                    aria-label="Вид адрес"
                    {...form.register(`stops.${index}.kind`)}
                    options={[
                      { value: 'pickup', label: STOP_KIND_BG.pickup },
                      { value: 'delivery', label: STOP_KIND_BG.delivery },
                    ]}
                  />
                  <span className="text-xs text-ink-faint">#{index + 1}</span>
                  <div className="ml-auto flex gap-1">
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      disabled={index === 0}
                      onClick={() => move(index, index - 1)}
                      aria-label="Нагоре"
                    >
                      <ArrowUp className="size-3.5" />
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      disabled={index === fields.length - 1}
                      onClick={() => move(index, index + 1)}
                      aria-label="Надолу"
                    >
                      <ArrowDown className="size-3.5" />
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      disabled={fields.length <= 2}
                      onClick={() => remove(index)}
                      aria-label={t.common.remove}
                    >
                      <Trash2 className="size-3.5" />
                    </Button>
                  </div>
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  <Field
                    label="Обект"
                    required
                    error={errors.stops?.[index]?.name?.message}
                    className="sm:col-span-2"
                  >
                    <PlaceAutocomplete
                      value={form.watch(`stops.${index}.name`) ?? ''}
                      country={form.watch(`stops.${index}.country`)}
                      onChange={(value) => form.setValue(`stops.${index}.name`, value)}
                      onSelect={(place) => applyPlace(index, place)}
                    />
                  </Field>

                  <Field label="Адрес" required error={errors.stops?.[index]?.address?.message}>
                    <Input {...form.register(`stops.${index}.address`)} />
                  </Field>
                  <Field label="Град" required error={errors.stops?.[index]?.city?.message}>
                    <Input {...form.register(`stops.${index}.city`)} />
                  </Field>
                  <Field label="Пощенски код">
                    <Input {...form.register(`stops.${index}.postcode`)} />
                  </Field>
                  <Field label="Държава" required error={errors.stops?.[index]?.country?.message}>
                    <Select
                      {...form.register(`stops.${index}.country`)}
                      options={COUNTRY_OPTIONS}
                    />
                  </Field>
                  <Field label="Прозорец от">
                    <DatePicker {...form.register(`stops.${index}.windowFrom`)} />
                  </Field>
                  <Field label="Прозорец до">
                    <DatePicker {...form.register(`stops.${index}.windowTo`)} />
                  </Field>
                  <Field label="Лице за контакт">
                    <Input {...form.register(`stops.${index}.contactName`)} />
                  </Field>
                  <Field label="Телефон">
                    <Input {...form.register(`stops.${index}.contactPhone`)} />
                  </Field>
                  <Field label="Референция на адреса">
                    <Input {...form.register(`stops.${index}.reference`)} />
                  </Field>
                  <div className="flex items-end">
                    <Checkbox
                      label={t.orders.hardDeadline}
                      {...form.register(`stops.${index}.isHardDeadline`)}
                    />
                  </div>
                  <Field label="Указания за шофьора" className="sm:col-span-2">
                    <Textarea rows={2} {...form.register(`stops.${index}.instructions`)} />
                  </Field>
                </div>
              </div>
            ))}

            {errors.stops?.message ? (
              <p className="text-xs text-danger">{errors.stops.message}</p>
            ) : null}

            <div className="flex gap-2">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                icon={<Plus className="size-3.5" />}
                onClick={() => append(emptyStop('pickup'))}
              >
                {t.orders.addPickup}
              </Button>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                icon={<Plus className="size-3.5" />}
                onClick={() => append(emptyStop('delivery'))}
              >
                {t.orders.addDelivery}
              </Button>
            </div>
          </CardBody>
        </Card>

        <div className="space-y-4">
          <Card>
            <CardHeader title="Клиент и цена" />
            <CardBody className="space-y-3">
              <Field label={t.orders.partner} required error={errors.partnerId?.message}>
                <Select
                  {...form.register('partnerId')}
                  placeholder="— избери —"
                  options={(partners.data?.items ?? [])
                    .filter((partner) => partner.isCustomer)
                    .map((partner) => ({ value: partner.id, label: partner.name }))}
                />
              </Field>
              <Field
                label={t.orders.customerRef}
                hint="Номерът, който спедиторът казва по телефона"
              >
                <Input {...form.register('customerRef')} />
              </Field>
              <Field
                label={`${t.orders.price} (в евроцентове)`}
                required
                error={errors.agreedPriceMinor?.message}
                hint="1 950,00 € се въвежда като 195000"
              >
                <Input
                  type="number"
                  step={1}
                  min={0}
                  className="font-mono tabular"
                  {...form.register('agreedPriceMinor', { valueAsNumber: true })}
                />
              </Field>
            </CardBody>
          </Card>

          <Card>
            <CardHeader title={t.orders.goods} />
            <CardBody className="space-y-3">
              <Field label="Описание" required error={errors.goodsDescription?.message}>
                <Textarea rows={2} {...form.register('goodsDescription')} />
              </Field>
              <Field
                label={`${t.orders.weight} (кг)`}
                required
                error={errors.totalWeightKg?.message}
              >
                <Input
                  type="number"
                  min={1}
                  max={3500}
                  className="font-mono tabular"
                  {...form.register('totalWeightKg', { valueAsNumber: true })}
                />
              </Field>
              <Field
                label="Обем (m³)"
                hint="С теглото решава колко заявки се побират в един бус. Д × Ш × В в метри, по броя колети."
              >
                <Input
                  type="number"
                  step="0.01"
                  min={0.01}
                  className="font-mono tabular"
                  {...form.register('volumeM3', { setValueAs: optionalNumber })}
                />
              </Field>
              <Field label="Брой колети">
                <Input
                  type="number"
                  min={1}
                  className="font-mono tabular"
                  {...form.register('packagesCount', { setValueAs: optionalNumber })}
                />
              </Field>
              <Field
                label={t.orders.declaredValue}
                hint="В евроцентове. Без нея обезщетението е 8.33 SDR/кг."
              >
                <Input
                  type="number"
                  min={0}
                  className="font-mono tabular"
                  {...form.register('declaredValueMinor', { setValueAs: optionalNumber })}
                />
              </Field>
              <Checkbox label={t.orders.tailLift} {...form.register('requiresTailLift')} />
              <Checkbox label={t.orders.adr} {...form.register('isAdr')} />
              {form.watch('isAdr') ? (
                <Field
                  label={t.orders.adrPoints}
                  hint="До 1000 точки се вози без ADR свидетелство и без табели (ADR 1.1.3.6)."
                >
                  <Input
                    type="number"
                    min={0}
                    className="font-mono tabular"
                    {...form.register('adrPoints', { setValueAs: optionalNumber })}
                  />
                </Field>
              ) : null}
              <Field label={t.common.notes}>
                <Textarea rows={2} {...form.register('notes')} />
              </Field>
            </CardBody>
          </Card>
        </div>
      </div>
    </form>
  );
}

/**
 * Спирките по позиция: съществуваща → PATCH, нова → POST, излишна → DELETE.
 * Пращат се само промените, за да не се пипа спирка, по която шофьорът вече
 * е отбелязал пристигане.
 */
async function syncStops(
  orderId: string,
  original: OrderStop[],
  next: OrderInput['stops'],
): Promise<void> {
  const sorted = [...original].sort((a, b) => a.sequence - b.sequence);
  for (const [index, stop] of next.entries()) {
    const current = sorted[index];
    const body = Object.fromEntries(Object.entries(stop).filter(([, v]) => v !== undefined && v !== ''));
    if (!current) {
      await api.post(`/orders/${orderId}/stops`, { ...body, sequence: index + 1 });
      continue;
    }
    const changed = Object.entries(body).some(([key, value]) => {
      const was = (current as unknown as Record<string, unknown>)[key];
      return was === undefined ? true : String(was ?? '') !== String(value ?? '');
    });
    if (changed) await api.patch(`/orders/${orderId}/stops/${current.id}`, body);
  }
  for (const extra of sorted.slice(next.length)) {
    await api.del(`/orders/${orderId}/stops/${extra.id}`);
  }
}
